import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Button,
  FormControl,
  MenuItem,
  TextField,
  Box,
  alpha,
  useTheme,
  Typography,
  Divider,
  Stack,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import DialogBase from "./DialogBase";
import { UtilityService } from "../../services/UtilityService";
import { useOrganisations } from "../../hooks/queries/useOrganisations";
import { useSelector } from "react-redux";
import { logger } from "../../utils/logger";
import {
  useDeviceBrands,
  useLNS,
  useAddDeviceManually,
} from "../../hooks/queries/useDevices";

export default function ManualKeysDialog(props) {
  const { open, handleClose } = props;
  const { t } = useTranslation("common");
  const [selectedOrganisationID, setOrganisationID] = React.useState();
  const [dev_eui, setDev_eui] = React.useState("");
  const [app_eui, setApp_eui] = React.useState("");
  const [app_key, setApp_key] = React.useState("");
  const [name, setName] = React.useState("");
  const [deviceBrand, setdeviceBrand] = React.useState(null);
  const [region, setRegion] = useState("EU868");
  const [selectedLNSID, setLNSID] = useState();
  const theme = useTheme();

  // Use TanStack Query hooks
  const { data: organisations = [] } = useOrganisations();
  const selectedOrganisation = useSelector(
    (state) => state.selectedOrganisation
  );
  const { data: deviceBrands = [] } = useDeviceBrands();
  const { data: lns = [] } = useLNS();
  const addDeviceMutation = useAddDeviceManually();

  // Fall back to the selected (or first) organisation until the user picks one
  const organisationID =
    selectedOrganisationID ||
    selectedOrganisation?.id ||
    organisations?.[0]?.id;

  // Custom text field styles
  const textFieldStyle = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      backgroundColor: alpha(theme.palette.background.paper, 0.8),
      "&:hover": {
        boxShadow: "0 0 0 2px rgba(25, 118, 210, 0.1)",
      },
      "&.Mui-focused": {
        boxShadow: "0 0 0 2px rgba(25, 118, 210, 0.2)",
      },
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: alpha(theme.palette.divider, 0.3),
    },
    "& .MuiInputLabel-root": {
      fontSize: "0.875rem",
      fontWeight: 500,
    },
  };

  // Fall back to the first LNS until the user picks one
  const lns_id = selectedLNSID || lns[0]?.id;

  async function onboardManualKeys() {
    if (!name || name === "") {
      UtilityService.updateSnackbarMessage(t("devices.name_required"));
      return;
    }
    if (!deviceBrand) {
      UtilityService.updateSnackbarMessage(t("common.device_type"));
      return;
    }

    //check dev_eui is valid
    if (!dev_eui || !dev_eui.match(/^[0-9a-fA-F]{16}$/)) {
      UtilityService.updateSnackbarMessage(t("devices.dev_eui_required"));
      return;
    }
    //check app_eui is valid
    if (!app_eui || !app_eui.match(/^[0-9a-fA-F]{16}$/)) {
      UtilityService.updateSnackbarMessage(t("devices.app_eui_required"));
      return;
    }
    //check app_key is valid
    if (!app_key || !app_key.match(/^[0-9a-fA-F]{32}$/)) {
      UtilityService.updateSnackbarMessage(t("devices.app_key_required"));
      return;
    }

    if (!lns_id) {
      UtilityService.updateSnackbarMessage(t("common.lns"));
      return;
    }

    try {
      await addDeviceMutation.mutateAsync({
        organisationId: organisationID,
        devEui: dev_eui,
        appEui: app_eui,
        appKey: app_key,
        lnsId: lns_id,
        region: region,
        deviceBrandId: deviceBrand.id,
        name: name,
      });

      UtilityService.updateSnackbarMessage(t("devices.device_added", { name }));
      clearFields();
      handleClose();
    } catch (error) {
      logger.error("Error adding device:", error);
      UtilityService.updateSnackbarMessage(
        t("common.error_adding", { name: "device" }) +
          ": " +
          (error.message || t("common.na"))
      );
    }
  }

  // Clear all fields when dialog is closed
  const clearFields = () => {
    setName("");
    setDev_eui("");
    setApp_eui("");
    setApp_key("");
    setRegion("EU868");
    setdeviceBrand(null);
    // Keep organisation and LNS as they were
  };

  // Clear fields when dialog is opened/closed
  useEffect(() => {
    if (!open) {
      clearFields();
    }
  }, [open]);

  return (
    <DialogBase
      open={open}
      handleClose={handleClose}
      title={t("common.manual_keys_title")}
      actions={
        <>
          <Button
            variant="outlined"
            onClick={handleClose}
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 500,
              px: 3,
              color:
                theme.palette.mode === "dark"
                  ? "#00c9a7"
                  : theme.palette.secondary.main,
              borderColor: alpha(
                theme.palette.mode === "dark"
                  ? "#00c9a7"
                  : theme.palette.secondary.main,
                0.5
              ),
              "&:hover": {
                borderColor:
                  theme.palette.mode === "dark"
                    ? "#00c9a7"
                    : theme.palette.secondary.main,
                backgroundColor: alpha(
                  theme.palette.mode === "dark"
                    ? "#00c9a7"
                    : theme.palette.secondary.main,
                  0.05
                ),
              },
            }}
          >
            {t("common.cancel")}
          </Button>

          <Button
            variant="contained"
            onClick={onboardManualKeys}
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              py: 1,
              px: 3,
              fontWeight: 500,
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "#00c9a7"
                  : theme.palette.primary.main,
              color: theme.palette.mode === "dark" ? "#000" : "#fff",
              "&:hover": {
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? alpha("#00c9a7", 0.9)
                    : alpha(theme.palette.primary.main, 0.9),
              },
            }}
          >
            {t("common.add_device")}
          </Button>
        </>
      }
    >
      <Box sx={{ p: 3 }}>
        <Stack spacing={3}>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <Autocomplete
              id="deviceBrand"
              options={deviceBrands || []}
              getOptionLabel={(option) => option.description || ""}
              value={deviceBrand}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("common.device_type")}
                  variant="outlined"
                  sx={textFieldStyle}
                  placeholder={t("common.device_type")}
                />
              )}
              onChange={(event, value) => {
                setdeviceBrand(value);
              }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
          </FormControl>

          <FormControl fullWidth>
            <TextField
              id="name"
              label={t("common.name")}
              variant="outlined"
              value={name}
              onChange={(event) => {
                setName(event.target.value);
              }}
              sx={textFieldStyle}
              placeholder={t("common.name")}
            />
          </FormControl>

          <FormControl fullWidth>
            <TextField
              id="organisation_id"
              label={t("common.organisation")}
              variant="outlined"
              select
              onChange={(event) => {
                setOrganisationID(event.target.value);
              }}
              value={organisationID || ""}
              sx={textFieldStyle}
            >
              {organisations && organisations.length > 0 ? (
                organisations.map((organisation) => (
                  <MenuItem key={organisation.id} value={organisation.id}>
                    {organisation.name}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>{t("common.no_organisations")}</MenuItem>
              )}
            </TextField>
          </FormControl>

          <Box
            sx={{
              p: 2.5,
              bgcolor: alpha(theme.palette.background.default, 0.4),
              borderRadius: "12px",
              border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                mb: 2,
                fontWeight: 600,
                color: alpha(theme.palette.text.primary, 0.7),
              }}
            >
              {t("common.dev_eui")} / {t("common.app_eui")} /{" "}
              {t("common.app_key")}
            </Typography>

            <Stack spacing={2}>
              <TextField
                id="dev_eui"
                label={t("common.dev_eui")}
                variant="outlined"
                fullWidth
                value={dev_eui || ""}
                onChange={(event) => {
                  setDev_eui(event.target.value);
                }}
                sx={textFieldStyle}
                placeholder={t("common.hex_16_chars")}
                helperText="Example: 70B3D57ED0051234"
              />

              <TextField
                id="app_eui"
                label={t("common.app_eui")}
                variant="outlined"
                fullWidth
                value={app_eui || ""}
                onChange={(event) => {
                  setApp_eui(event.target.value);
                }}
                sx={textFieldStyle}
                placeholder={t("common.hex_16_chars")}
                helperText="Example: 70B3D57ED0051234"
              />

              <TextField
                id="app_key"
                label={t("common.app_key")}
                variant="outlined"
                fullWidth
                value={app_key || ""}
                onChange={(event) => {
                  setApp_key(event.target.value);
                }}
                sx={textFieldStyle}
                placeholder={t("common.hex_32_chars")}
                helperText="Example: 2B7E151628AED2A6ABF7158809CF4F3C"
              />

              <TextField
                id="Region"
                label={t("common.region")}
                variant="outlined"
                required
                select
                value={region}
                onChange={(e) => {
                  setRegion(e.target.value);
                }}
                sx={textFieldStyle}
              >
                <MenuItem value="EU868">EU868</MenuItem>
                <MenuItem value="US915">US915</MenuItem>
                <MenuItem value="AU915">AU915</MenuItem>
              </TextField>

              <TextField
                id="lns"
                label={t("common.lns")}
                variant="outlined"
                required
                select
                value={lns_id || ""}
                onChange={(e) => {
                  setLNSID(e.target.value);
                }}
                sx={textFieldStyle}
              >
                {lns && lns.length > 0 ? (
                  lns.map((lnsItem) => (
                    <MenuItem key={lnsItem.id} value={lnsItem.id}>
                      {lnsItem.name}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>{t("common.lns")}</MenuItem>
                )}
              </TextField>
            </Stack>
          </Box>
        </Stack>
      </Box>

      <Divider />
    </DialogBase>
  );
}
